import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PaymentElement, ExpressCheckoutElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Loader2, ShieldCheck } from "lucide-react";
import { API } from "@/config";

// Stripe Elements options — kept as module-level constants so their identity
// stays stable across renders. New object references trigger the underlying
// iframe to re-init, which on iOS Safari can leave the payment fields blank.
const EXPRESS_CHECKOUT_OPTIONS = {
  buttonHeight: 48,
  buttonType: { applePay: "check-out", googlePay: "checkout" },
  // wallets default is 'auto' for both — show when browser supports it.
};
const PAYMENT_ELEMENT_OPTIONS = { layout: "tabs" };

/**
 * PaymentForm — Stripe Elements section of the checkout form.
 * Renders (when supported) the Express Checkout wallet buttons on top,
 * an "OR PAY WITH CARD" divider, the tabbed PaymentElement, and the
 * "Pay $XX" submit button. Handles:
 *   - hiding the Express slot when no wallet is available (mobile domains
 *     without verified Apple Pay collapse the iframe to 0-8px otherwise)
 *   - loading skeleton until PaymentElement is `ready` (avoids the
 *     3-10s blank period on slow mobile networks)
 *   - iOS Safari safe wrapper — .glass-solid parent (no backdrop-filter)
 *     is applied by the caller.
 *
 * All the gating (name+email+phone+consents) is passed as `gateReasons`
 * — the button is disabled with a friendly hint listing what to fix.
 */
export const PaymentForm = ({
  reservationId, quote, submitting, setSubmitting,
  firstName, lastName, email, phone, smsOptedIn, ackAccepted = {}, gateReasons,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [pmReady, setPmReady] = useState(false);
  const [walletsAvailable, setWalletsAvailable] = useState(false);
  // Below Stripe's $0.50 minimum (e.g. 100% promo) — nothing to charge, so the
  // card fields are hidden and the booking completes server-side as $0.
  const isFree = !!quote && Number(quote.total) < 0.5;

  const syncBeforeConfirm = async () => {
    await axios.post(`${API}/checkout/update`, {
      reservation_id: reservationId,
      customer_name: `${firstName} ${lastName}`.trim(),
      customer_email: email,
      customer_phone: phone,
      terms_accepted: true,
      sms_opted_in: !!smsOptedIn,
      sleep_ack_accepted: !!ackAccepted.sleep,
      mobility_ack_accepted: !!ackAccepted.mobility,
    });
  };

  const returnUrl = `${window.location.origin}/booking-success?reservation_id=${reservationId}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (gateReasons.length > 0) { toast.error("Please complete: " + gateReasons.join(" · ")); return; }
    if (isFree) {
      setSubmitting(true);
      try {
        await syncBeforeConfirm();
        await axios.post(`${API}/checkout/complete-free`, { reservation_id: reservationId });
        window.location.assign(`${window.location.origin}/booking-success?reservation_id=${reservationId}&free=1`);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Something went wrong.");
        setSubmitting(false);
      }
      return;
    }
    if (!stripe || !elements) return;
    setSubmitting(true);
    try {
      await syncBeforeConfirm();
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: returnUrl,
          payment_method_data: {
            billing_details: { name: `${firstName} ${lastName}`.trim(), email, phone },
          },
        },
      });
      if (error) { toast.error(error.message || "Payment failed. Please try again."); setSubmitting(false); }
      // On success Stripe redirects — no code runs here.
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong.");
      setSubmitting(false);
    }
  };

  return (
    <div className="glass-solid p-5 sm:p-6">
      <h2 className="font-serif text-2xl text-[#FFD166] mb-4">Payment</h2>

      {/* Express Checkout — hidden entirely when no wallet is offered so we don't
          leave a lonely "OR PAY WITH CARD" divider above empty space. */}
      {isFree && (
        <div className="rounded-xl border border-[#FFD166]/30 bg-[#FFD166]/10 px-4 py-3 text-sm text-[#F6F1E4] mb-2" data-testid="checkout-free-notice">
          Your promo covers the full rental — <b>$0.00 due today</b>. No card needed; just complete your booking below.
        </div>
      )}
      <div className={walletsAvailable && !isFree ? "mb-4" : "hidden"} data-testid="express-checkout-slot">
        <ExpressCheckoutElement
          data-testid="express-checkout-element"
          onReady={(evt) => {
            const methods = evt?.availablePaymentMethods || {};
            const available = Object.values(methods).some(Boolean);
            setWalletsAvailable(available);
          }}
          onConfirm={async (event) => {
            if (gateReasons.length > 0) {
              toast.error("Please complete: " + gateReasons.join(" · "));
              return;
            }
            // Sync order details to backend before confirming with Stripe.
            try {
              await syncBeforeConfirm();
            } catch (syncErr) {
              toast.error(syncErr.response?.data?.detail || "Couldn't sync order details. Please try again.");
              return;
            }
            // confirmPayment returns { error } — it does NOT throw on payment failure.
            // clientSecret is already in the Elements context (passed to the <Elements> provider).
            const { error } = await stripe.confirmPayment({
              elements,
              confirmParams: { return_url: returnUrl },
            });
            if (error) {
              // immediate errors: incomplete details, card declined, etc.
              toast.error(error.message || "Wallet payment failed. Please try again.");
            }
            // On success Stripe redirects to return_url — no code runs here.
          }}
          options={EXPRESS_CHECKOUT_OPTIONS}
        />
        <div className="relative my-4 text-center" data-testid="wallets-divider">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#FFD166]/15" /></div>
          <span className="relative bg-[#0F1E2C] px-3 text-[10px] uppercase tracking-widest text-[#B9C8D5]">or pay with card</span>
        </div>
      </div>

      <div className={isFree ? "hidden" : "relative"} data-testid="stripe-payment-element">
        {!pmReady && (
          <div className="absolute inset-0 rounded-xl bg-[#0A1A26]/60 flex flex-col items-center justify-center gap-3 z-10 pointer-events-none animate-pulse" data-testid="payment-loading">
            <Loader2 className="w-6 h-6 text-[#FFD166] animate-spin" />
            <p className="text-xs text-[#B9C8D5]">Summoning secure card fields…</p>
          </div>
        )}
        {/* Reserve min-height so the skeleton doesn't jump layout when Stripe mounts. */}
        <div style={{ minHeight: pmReady ? 0 : 260 }}>
          <PaymentElement
            options={PAYMENT_ELEMENT_OPTIONS}
            onReady={() => setPmReady(true)}
            onLoadError={(evt) => {
              setPmReady(true);
              toast.error("Card entry couldn't load — please refresh. (" + (evt?.error?.message || "network error") + ")");
            }}
          />
        </div>
      </div>

      <button
        type="button"
        onClick={handleSubmit}
        disabled={(!isFree && !stripe) || submitting || gateReasons.length > 0}
        className="btn-primary w-full mt-5 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="checkout-pay-btn"
      >
        {submitting ? (isFree ? "Completing booking…" : "Processing payment…") : isFree ? "Complete booking · $0.00" : (quote ? `Pay $${quote.total.toFixed(2)}` : "Pay")}
      </button>
      {gateReasons.length > 0 && (
        <p className="text-[11px] text-[#F4A93A] mt-2 text-center" data-testid="checkout-gate-hint">
          To pay: {gateReasons.join(" · ")}.
        </p>
      )}
      <p className="text-xs text-[#B9C8D5] mt-3 text-center flex items-center justify-center gap-1">
        <ShieldCheck className="w-3 h-3" /> Secured by Stripe · Cancel anytime with Enchanted Peace of Mind
      </p>
    </div>
  );
};

export default PaymentForm;
